// Shaka Player 5.x calls `Map.prototype.getOrInsertComputed` (TC39 "Upsert"
// proposal) inside its event-target plumbing, fired synchronously from
// `new shaka.Player()`. That method only landed in Safari 18.4+ / recent
// Chrome & Firefox, so on iOS Safari < 18.4 the player throws on mount
// (`TypeError: this.g.getOrInsertComputed is not a function`).
//
// Shaka declines to ship this polyfill themselves — they treat the dependency
// as working as intended (shaka-project/shaka-player#9646) — so we install it
// before any player code runs. Imported first in src/main.tsx.
//
// No-op on engines that already implement the proposal.

interface Upsertable<TKey, TValue> {
  has: (key: TKey) => boolean;
  get: (key: TKey) => TValue | undefined;
  set: (key: TKey, value: TValue) => unknown;
}

function getOrInsert<TKey, TValue>(
  this: Upsertable<TKey, TValue>,
  key: TKey,
  value: TValue,
): TValue {
  if (this.has(key)) return this.get(key) as TValue;
  this.set(key, value);
  return value;
}

function getOrInsertComputed<TKey, TValue>(
  this: Upsertable<TKey, TValue>,
  key: TKey,
  callback: (key: TKey) => TValue,
): TValue {
  if (this.has(key)) return this.get(key) as TValue;
  const value = callback(key);
  this.set(key, value);
  return value;
}

const define = (target: object, name: string, fn: unknown) => {
  if (name in target) return;
  Object.defineProperty(target, name, {
    value: fn,
    writable: true,
    enumerable: false,
    configurable: true,
  });
};

define(Map.prototype, "getOrInsert", getOrInsert);
define(Map.prototype, "getOrInsertComputed", getOrInsertComputed);
define(WeakMap.prototype, "getOrInsert", getOrInsert);
define(WeakMap.prototype, "getOrInsertComputed", getOrInsertComputed);
